import { matchCommentsWidget } from '@sky-it-livedata/livedata-live-match-pages-widgets';
import { IMatchComments, MatchComments } from '@sky-it-livedata/livedata-sports-design-system';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useReducer } from 'react';

import { IModelWrapper } from '../../typings/common-types';
import { getModelFromUseService, isEmptyObject, isValidObject } from '../../utils/props-validator';
import { useService } from '../hooks/useService';

import {
  ID_WRAPPER_COMMENTS,
  defaultMatchComments,
  getNewPostsIndex,
  getPageNumber,
  getUrlWithoutSearchParams,
  optionsScrollTo,
} from './match-comments-helpers';

export interface IMatchCommentsWrapper extends IModelWrapper<IMatchComments> {}

const MatchCommentsWrapper: React.FC<IMatchCommentsWrapper> = ({ query, initialModel, live }) => {
  const { model } = useService({
    live,
    query: {
      ...query,
      page: getPageNumber(query.page ?? 1),
    },
    widget: matchCommentsWidget,
    initialValue: initialModel,
  });
  const componentModel: IMatchComments = getModelFromUseService(model);

  // Manage ACTUAL vs NEW state (coming from useService)
  const reducerMatchComments = (prev: IMatchComments, next: IMatchComments): IMatchComments => {
    const latestPostIndex: number = getNewPostsIndex(prev.comments ?? [], next.comments ?? []);
    const isBadgeReset: boolean =
      prev.updatesBadge && next.updatesBadge ? prev.updatesBadge.isVisible! && !next.updatesBadge.isVisible! : false;

    return {
      ...next,
      newPostsCount: (prev.newPostsCount ?? 0) + latestPostIndex,
      updatesBadge: {
        isVisible: isBadgeReset ? false : latestPostIndex > 0,
        count: isBadgeReset ? 0 : (prev.updatesBadge?.count ?? 0) + latestPostIndex,
        onClick: () => onClickBadge(next),
      },
    };
  };
  const [updatedPayload, updatePayload] = useReducer(reducerMatchComments, componentModel); // INITAL STATE

  const onClickBadge = useCallback((payload) => {
    // UPDATE STATE after CLICK (resets UPDATESBADGE)
    const { updatesBadge } = defaultMatchComments;
    updatePayload({
      ...payload,
      updatesBadge,
    });
    // SCROLL TO "#match-comments-top" div or PAGE #1 RELOAD?
    if (getPageNumber(1) === 1) {
      const elementComments: HTMLElement | null = document.getElementById(ID_WRAPPER_COMMENTS);
      elementComments && elementComments.scrollIntoView(optionsScrollTo);
    } else {
      location.replace(getUrlWithoutSearchParams());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Update STATE with useReducer() whenever useService() updates componentModel
    if (isValidObject(componentModel)) {
      updatePayload(componentModel);
    }
  }, [componentModel]); // eslint-disable-line react-hooks/exhaustive-deps

  // DEBUG ONLY
  // useEffect(() => {
  //   console.log('MatchComments-DEBUG', { updatedPayload });
  // }, [updatedPayload]); // eslint-disable-line react-hooks/exhaustive-deps

  // console.log('MatchComments', { updatedPayload });
  return !isEmptyObject(updatedPayload) ? <MatchComments {...updatedPayload} /> : null;
};

MatchCommentsWrapper.propTypes = {
  initialModel: PropTypes.any.isRequired,
  live: PropTypes.bool,
  query: PropTypes.object.isRequired,
};

MatchCommentsWrapper.defaultProps = {
  live: true,
};

export default MatchCommentsWrapper;
